import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUnlock } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &.error {
    border-color: var(--error-color);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--primary-color);
  color: var(--text-inverse);
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: var(--background-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--background-tertiary);
    border-color: var(--border-dark);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const ToggleCard = styled.label`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--border-color);
  background: var(--background-secondary);
  cursor: pointer;
`;

const ToggleContent = styled.div`
  display: flex;
  gap: 0.75rem;
  color: var(--text-primary);
`;

const ToggleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ToggleTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
`;

const ToggleDescription = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  line-height: 1.4;
`;

const ToggleInput = styled.input`
  width: 2.75rem;
  height: 1.5rem;
  appearance: none;
  border-radius: 999px;
  background: var(--border-color);
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 0.1875rem;
    left: 0.1875rem;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    background: #fff;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  &:checked {
    background: var(--primary-color);
  }

  &:checked::after {
    transform: translateX(1.25rem);
  }
`;

function AddCourseModal({ isOpen, onClose, onCourseAdded }) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    instructorId: '',
    maxStudents: 25,
    startDate: '',
    endDate: '',
    openEnrollment: true
  });
  const [instructors, setInstructors] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchInstructors();
    }
  }, [isOpen]);

  const fetchInstructors = async () => {
    try {
      const response = await apiService.getInstructors();
      setInstructors(response.instructors || []);
    } catch (error) {
      logger.error('Error fetching instructors', { error });
      setInstructors([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Course code is required';
    } else if (formData.code.length > 20) {
      newErrors.code = 'Course code must be 20 characters or less';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Course name is required';
    }

    if (!formData.instructorId) {
      newErrors.instructorId = 'Instructor is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (formData.maxStudents < 1 || formData.maxStudents > 200) {
      newErrors.maxStudents = 'Maximum students must be between 1 and 200';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await apiService.createCourse(formData);
      
      onCourseAdded(response.course);
      onClose();
      
      // Reset form
      setFormData({
        code: '',
        name: '',
        description: '',
        instructorId: '',
        maxStudents: 25,
        startDate: '',
        endDate: '',
        openEnrollment: true
      });
      setErrors({});
      
    } catch (error) {
      logger.error('Error creating course', { error, formData });
      setErrors({ submit: 'Failed to create course. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Add New Course</ModalTitle>
              <CloseButton onClick={onClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <Row>
                <FormGroup>
                  <Label>Course Code *</Label>
                  <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    className={errors.code ? 'error' : ''}
                    placeholder="e.g., FAITH101"
                    maxLength={20}
                  />
                  {errors.code && <ErrorMessage>{errors.code}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>Course Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={errors.name ? 'error' : ''}
                    placeholder="e.g., Introduction to Faith Studies"
                  />
                  {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                </FormGroup>
              </Row>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter course description..."
                />
              </FormGroup>

              <Row>
                <FormGroup>
                  <Label>Instructor *</Label>
                  <Select
                    value={formData.instructorId}
                    onChange={(e) => handleInputChange('instructorId', e.target.value)}
                    className={errors.instructorId ? 'error' : ''}
                  >
                    <option value="">Select an instructor</option>
                    {instructors.map(instructor => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.first_name} {instructor.last_name}
                      </option>
                    ))}
                  </Select>
                  {errors.instructorId && <ErrorMessage>{errors.instructorId}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>Maximum Students</Label>
                  <Input
                    type="number"
                    value={formData.maxStudents}
                    onChange={(e) => handleInputChange('maxStudents', parseInt(e.target.value))}
                    className={errors.maxStudents ? 'error' : ''}
                    min="1"
                    max="200"
                  />
                  {errors.maxStudents && <ErrorMessage>{errors.maxStudents}</ErrorMessage>}
                </FormGroup>
              </Row>

              <Row>
                <FormGroup>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className={errors.startDate ? 'error' : ''}
                  />
                  {errors.startDate && <ErrorMessage>{errors.startDate}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className={errors.endDate ? 'error' : ''}
                  />
                  {errors.endDate && <ErrorMessage>{errors.endDate}</ErrorMessage>}
                </FormGroup>
              </Row>

              <ToggleCard>
                <ToggleContent>
                  <FiUnlock size={18} />
                  <ToggleText>
                    <ToggleTitle>Open Enrollment</ToggleTitle>
                    <ToggleDescription>
                      Keep this course visible in the course catalog for student registration regardless of the enrollment period.
                    </ToggleDescription>
                  </ToggleText>
                </ToggleContent>
                <ToggleInput
                  type="checkbox"
                  checked={Boolean(formData.openEnrollment)}
                  onChange={(e) => handleInputChange('openEnrollment', e.target.checked)}
                />
              </ToggleCard>

              {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

              <ButtonGroup>
                <SecondaryButton type="button" onClick={onClose} disabled={loading}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Course'}
                </PrimaryButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default AddCourseModal;
